import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import React, {useState, useEffect} from 'react';
import NumberToDate from './NumberToDate';
import {BatterySymbol} from './BatterySymbol';
import Table from '../../../components/common/Table';
import { TableActionColumn } from '../../../components/common/TableActionColumn';
import { useDashboardContext } from '../context/dashboard.context';
import { ACTIONS_LIST } from '../constant/Dashboard.constant';

const MainContent = styled.div`
  borer-bottom: none;
  border: 1px solid #e0e3e9;
  margin-top: 10px;
  @media screen and (max-width: 750px) {
    margin-top: 60px;
  }
`
const CuzCell = styled.p`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 0px 0px 16px;;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

const CustomHeader = styled.p`
  padding: 0px 0px 0px 16px;
  font-weight: bold;
  text-wrap-mode: nowrap;
  align-self: 'center';
  text-align: ${props => (props.left ? 'left' : 'center')};
`;

const TableHeader = styled.div`
  font-size: 16px;
  line-height: 24px;
`
const DEFAULT_ROWS_PER_PAGE = 10

export const InUseDeviceListTable = ({tableHeader, fetchData, updateDeviceStatus}) => {
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE)
  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [orderBy, setOrderBy] = useState('reportedAt');
  const [orderDirection, setOrderDirection] = useState('desc');
  const { inUseDeviceList, setInUseDeviceList, inUseCount, inUseDeviceCursor, setInUseDeviceCursor} = useDashboardContext()
  const [itemCount, setItemCount] = useState(inUseCount)
  
  const {t} = useTranslation()

  useEffect(() => {setItemCount(inUseCount)}, [inUseCount])


  const next = async() => {
    if (!inUseDeviceCursor?.last) return;
    setIsLoading(true);
    setCurrentPage(prev => prev + 1);
    try {
      const { devices, cursors: newCursors } = await fetchData({
        limit: pageSize,
        orderBy,
        orderDirection,
        startAfterDoc: inUseDeviceCursor?.last,
        totalCount: inUseCount,
        state: 'inUse'
      });

      if (devices?.length > 0) {
        setInUseDeviceList(devices);
        setInUseDeviceCursor(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching next page:', error);
      setIsLoading(false);
    }
  }

  const prev = async() => {
    if (!inUseDeviceCursor?.first) return;
    setIsLoading(true);
    setCurrentPage(prev => prev - 1);
    try {
      const { devices, cursors: newCursors } = await fetchData({
        limit: pageSize,
        orderBy,
        orderDirection,
        endBeforeDoc: inUseDeviceCursor?.first,
        totalCount: inUseCount,
        state: 'inUse'
      });
      if (devices?.length > 0) {
        setInUseDeviceList(devices);
        setInUseDeviceCursor(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching previous page:', error);
      setIsLoading(false);
    }
  }

  const handleFirstPage = async() => {
    setIsLoading(true);
    setCurrentPage(0);
    try {
      const { devices, cursors: newCursors } = await fetchData({
        limit: pageSize,
        orderBy,
        orderDirection,
        totalCount: inUseCount,
        isFirstPage: true,
        state: 'inUse'
      });
      setInUseDeviceList(devices);
      setInUseDeviceCursor(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching first page:', error);
      setIsLoading(false);
    }
  }

  const handleLastPage = async() => {
    setIsLoading(true);
    try {
      const { devices, cursors: newCursors } = await fetchData({
        limit: pageSize,
        orderBy,
        orderDirection,
        totalCount: inUseCount,
        isLastPage: true,
        state: 'inUse'
      }, true);
      if (devices?.length > 0) {
        setInUseDeviceList(devices);
        setInUseDeviceCursor(newCursors);
        const totalPages = Math.ceil(itemCount / pageSize);
        const lastPageNumber = Math.max(0, totalPages - 1);
        setCurrentPage(lastPageNumber);
      } else {
        setInUseDeviceList([]);
        setInUseDeviceCursor(null);
        setCurrentPage(0);
      }
  
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching last page:', error);
      setInUseDeviceList([]);
      setInUseDeviceCursor(null);
      setCurrentPage(0);
      setIsLoading(false);
    }
  }

  const handleSort = async(field, direction) => {  
    setIsLoading(true);
    setCurrentPage(0);
    setInUseDeviceCursor(null);
    try {
      setOrderBy(field);
      setOrderDirection(direction);

      const { devices, cursors: newCursors } = await fetchData({
        limit: pageSize,
        orderBy: field,
        orderDirection: direction,
        totalCount: inUseCount,
        state: 'inUse'
      });

      setInUseDeviceList(devices);
      setItemCount(inUseCount);
      setInUseDeviceCursor(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setIsLoading(false);
    }
  }

  const handleRowPerPageChange = async(event) => {
    const newSize = event.target.value;
    setPageSize(newSize);
    setInUseDeviceCursor(null);
    setIsLoading(true);
    const { devices, cursors: newCursors } = await fetchData({limit: newSize, orderBy, orderDirection, totalCount: inUseCount, state: 'inUse'});
    setInUseDeviceList(devices);
    setInUseDeviceCursor(newCursors);
    setIsLoading(false);
  }

  const columns = [
    {
      title: <CustomHeader> {t('Mo_Dash.placeholders.devicename')}</CustomHeader>,
      field: '__name__',
      searchable: false,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.__name__}</CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('battery')}</CustomHeader>,
      field: 'batteryLevel',
      sorting: true,
      searchable: false,
      render: rowData => (
          <CuzCell><BatterySymbol battery={rowData.batteryLevel} /></CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('site.siteName')}</CustomHeader>,
      field: 'siteName',
      searchable: false,
      sorting: true,
      render: rowData => (
        <CuzCell>{t(rowData.siteName)}</CuzCell>
      ),
    },
    {
      title: <CustomHeader>{t('username')}</CustomHeader>,
      field: 'userId',
      sorting: true,
      render: (rowData) => {
        return (
          <span>
            <CuzCell>{rowData.userId}</CuzCell>
          </span>
        )
      }
    },
    {
      title: <CustomHeader>{t('kiosk')}</CustomHeader>,
      field: 'kioskName',
      sorting: true,
      render: (rowData) => {
        return (
          <span>
            <CuzCell>{rowData.kioskName}</CuzCell>
          </span>
        )
      }
    },
    {
      title: <CustomHeader>{t('lastUpdated')}</CustomHeader>,
      field: 'reportedAt',
      sorting: true,
      render: (rowData) => {
        return (
          <span>
            <CuzCell>
              <NumberToDate
                style={{fontSize: '14px'}}
                timestamp={rowData.reportedAt}
              />
            </CuzCell>
          </span>
        )
      }
    }
  ]

  return (
    <div>
      <TableHeader>{tableHeader}</TableHeader>
      <MainContent>
      <Table
          columns={columns}
          data={inUseDeviceList}
          isLoading={isLoading}
          orderBy={orderBy}
          orderDirection={orderDirection}
          isSearchActive={false}
          onSort={(field, direction) =>  {
            handleSort(field, direction)
          }}
          // actions={[
          //   rowData => ({
          //     icon: () => <TableActionColumn row={rowData} actionClick={updateDeviceStatus} menuList={ACTIONS_LIST} />,
          //     onClick: (event, rowData) => {}
          //   })
          // ]}
          pagination={{
            itemCount,
            currentPage,
            rowsPerPage: pageSize,
            rowsPerPageOptions: [10, 20, 50],
            onNextPageClicked: next,
            onPrevPageClicked: prev,
            onFirstPageClicked: handleFirstPage,
            onLastPageClicked: handleLastPage,
            onChangeRowsPerPage: handleRowPerPageChange,
            hidePagination: itemCount <= DEFAULT_ROWS_PER_PAGE
          }}
          emptyMessage={t('messages.error.err56')}
        />
      </MainContent>
    </div>
  )
}
